/*
# -----------------------------------------------------------------------------
#	Project: HSE Genius®
#	Client ID: 834215
#	Registration Code: IT-2025-ZR319-MQ744
#	Author: KaimakiWeb
#	Website: https://kaimakiweb.com
#	Copyright (c) 2024-2026 Kaimaki Web. All rights reserved.
#
#	This software and its source code are the property of KaimakiWeb.
#	Unauthorized copying, modification, distribution, or use of this software,
#	via any medium, is strictly prohibited unless prior written permission
#	is obtained from KaimakiWeb.
# -----------------------------------------------------------------------------
*/

import React, { lazy } from "react";
import { Navigate } from "react-router-dom";

import RouteEnum from "../enums/route.enum";

const NotFoundPage = lazy(() => import("../../components/pages/not-found"));
const SignInPage = lazy(() => import("../../components/auth/sign-in"));
const SignupPage = lazy(() => import("../../components/pages/signup"));
const RegistrationPage = lazy(() =>
  import("../../components/pages/registration")
);
const UploadingFilesPage = lazy(() =>
  import("../../components/pages/uploading-files")
);
const parsedFilesWrapperImport = () =>
  import("../../components/pages/parsed-files/parsed-files-wrapper");
const parsedFilesTableImport = () =>
  import("../../components/pages/parsed-files/parsed-files-table");

const ParsedFilesWrapper = lazy(parsedFilesWrapperImport);

/** Preload parsed-files chunks on hover so /parsed-files loads faster. */
export const preloadParsedFiles = () => {
  Promise.all([parsedFilesWrapperImport(), parsedFilesTableImport()]);
};
const EditProfilePage = lazy(() =>
  import("../../components/pages/edit-profile")
);
const ConfigPage = lazy(() => import("../../components/pages/config"));
const CustomersPage = lazy(() => import("../../components/pages/customers"));
const AdminPage = lazy(() => import("../../components/pages/admin"));
const AuditPage = lazy(() => import("../../components/pages/audit"));
const AcceptInvitePage = lazy(() =>
  import("../../components/pages/accept-invite")
);
const ResetPasswordRequestPage = lazy(() =>
  import("../../components/pages/reset-password/request")
);
const ResetPasswordConfirmPage = lazy(() =>
  import("../../components/pages/reset-password/confirm")
);

const OtpVerifyPage = lazy(() =>
  import("../../components/pages/reset-password/verify")
);

const MfaVerifyPage = lazy(() =>
  import("../../components/auth/mfa-verify")
);
const MfaSetupPage = lazy(() =>
  import("../../components/auth/mfa-setup")
);
const OnboardingCreateCompanyPage = lazy(() =>
  import("../../components/auth/onboarding-create-company")
);
const OnboardingPendingApprovalPage = lazy(() =>
  import("../../components/auth/onboarding-pending-approval")
);
const ConsultantApprovalsPage = lazy(() =>
  import("../../components/pages/staff/consultant-approvals")
);
const AnalyticsPage = lazy(() =>
  import("../../components/pages/analytics")
);
const WhatsNewPage = lazy(() =>
  import("../../components/pages/whats-new")
);

const routes = [
  {
    path: RouteEnum.HOME,
    Component: () => <Navigate to={RouteEnum.UPLOADING_FILES} replace />,
  },
  {
    path: RouteEnum.LOGIN,
    Component: SignInPage,
    publicOnly: true,
    redirectTo: RouteEnum.UPLOADING_FILES,
  },
  {
    path: RouteEnum.SIGNUP,
    Component: SignupPage,
    publicOnly: true,
    redirectTo: RouteEnum.UPLOADING_FILES,
  },
  {
    path: RouteEnum.REGISTER,
    Component: RegistrationPage,
    publicOnly: true,
    redirectTo: RouteEnum.UPLOADING_FILES,
  },
  {
    path: RouteEnum.ACCEPT_INVITE,
    Component: AcceptInvitePage,
    publicOnly: true,
    authOnly: false,
    redirectTo: RouteEnum.LOGIN,
  },
  {
    path: RouteEnum.RESET_PASSWORD,
    Component: ResetPasswordRequestPage,
    publicOnly: true,
    redirectTo: RouteEnum.UPLOADING_FILES,
  },
  {
    path: RouteEnum.RESET_PASSWORD_VERIFY,
    Component: OtpVerifyPage,
    publicOnly: true,
    redirectTo: RouteEnum.UPLOADING_FILES,
  },
  {
    path: RouteEnum.RESET_PASSWORD_CONFIRM,
    Component: ResetPasswordConfirmPage,
    publicOnly: true,
    redirectTo: RouteEnum.UPLOADING_FILES,
  },
  {
    path: RouteEnum.MFA_VERIFY,
    Component: MfaVerifyPage,
    publicOnly: true,
    redirectTo: RouteEnum.UPLOADING_FILES,
  },
  {
    path: RouteEnum.MFA_SETUP,
    Component: MfaSetupPage,
    publicOnly: true,
    redirectTo: RouteEnum.UPLOADING_FILES,
  },
  {
    // Standalone onboarding for an authenticated user without a company.
    // authOnly: true so unauthenticated users get bounced to /login.
    // requiresSubscription: false so the missing subscription doesn't bounce them out.
    path: RouteEnum.ONBOARDING_CREATE_COMPANY,
    Component: OnboardingCreateCompanyPage,
    authOnly: true,
    requiresSubscription: false,
    redirectTo: RouteEnum.LOGIN,
  },
  {
    // Standalone "pending approval" screen for a user whose self-registered
    // company has not yet been approved by HSE Genius staff.
    path: RouteEnum.ONBOARDING_PENDING_APPROVAL,
    Component: OnboardingPendingApprovalPage,
    authOnly: true,
    requiresSubscription: false,
    redirectTo: RouteEnum.LOGIN,
  },
  {
    path: RouteEnum.UPLOADING_FILES,
    Component: UploadingFilesPage,
    authOnly: true,
    requiresSubscription: true,
    redirectTo: RouteEnum.LOGIN,
  },
  {
    path: RouteEnum.PARSED_FILES,
    Component: ParsedFilesWrapper,
    authOnly: true,
    requiresSubscription: true,
    redirectTo: RouteEnum.LOGIN,
  },
  {
    path: RouteEnum.PARSED_FILES_VIEW_VALIDATION,
    Component: ParsedFilesWrapper,
    authOnly: true,
    requiresSubscription: true,
    redirectTo: RouteEnum.LOGIN,
  },
  {
    path: RouteEnum.PARSED_FILES_VIEW_HISTORY,
    Component: ParsedFilesWrapper,
    authOnly: true,
    requiresSubscription: true,
    redirectTo: RouteEnum.LOGIN,
  },
  {
    path: RouteEnum.EDIT_PROFILE,
    Component: EditProfilePage,
    authOnly: true,
    redirectTo: RouteEnum.LOGIN,
  },
  {
    path: RouteEnum.CONFIG,
    Component: ConfigPage,
    authOnly: true,
    requiresSubscription: true,
    redirectTo: RouteEnum.LOGIN,
  },
  {
    path: RouteEnum.CUSTOMERS,
    Component: CustomersPage,
    authOnly: true,
    requiresSubscription: true,
    redirectTo: RouteEnum.LOGIN,
  },
  {
    path: RouteEnum.ADMIN,
    Component: AdminPage,
    authOnly: true,
    redirectTo: RouteEnum.LOGIN,
  },
  {
    path: RouteEnum.COMPANY,
    Component: AdminPage,
    authOnly: true,
    requiresSubscription: false,
    redirectTo: RouteEnum.LOGIN,
  },
  {
    path: RouteEnum.AUDIT,
    Component: AuditPage,
    authOnly: true,
    requiresSubscription: false,
    redirectTo: RouteEnum.LOGIN,
  },
  {
    path: RouteEnum.STAFF_CONSULTANTS,
    Component: ConsultantApprovalsPage,
    authOnly: true,
    requiresSubscription: false,
    redirectTo: RouteEnum.LOGIN,
  },
  {
    path: RouteEnum.ANALYTICS,
    Component: AnalyticsPage,
    authOnly: true,
    requiresSubscription: false,
    redirectTo: RouteEnum.LOGIN,
  },
  {
    path: RouteEnum.WHATS_NEW,
    Component: WhatsNewPage,
    authOnly: true,
    requiresSubscription: true,
    redirectTo: RouteEnum.LOGIN,
  },
  { path: RouteEnum.NOT_FOUND, Component: NotFoundPage },
  {
    path: "*",
    Component: ({ location }) => {
      if (location?.pathname?.startsWith("/accept_invite=true")) {
        return null;
      }
      return <Navigate to={RouteEnum.UPLOADING_FILES} replace />;
    },
  },
];

export default routes;
